import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getLanguageString as _ } from "misc/lang";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import cssLayout from "../layout.module.scss";
import css from "./users2screen.module.scss";
import { Organisation } from "model/organisation";
import { Home } from "model/home";
import { User } from "model/user";
import {
  apiGetAccountList,
  apiGetUserList,
  apiUserDelete,
  apiUserCreate,
} from "net/requests";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import Search from "components/search/Search";
import DataTable, { Column } from "components/datatable/DataTable";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import TextInput from "components/form/TextInput";
import CloseIcon from "@mui/icons-material/Close";
import QrPopup from "screens/sub-organisations/QrPopup";
import { getQRImageDataURL, savePNGFile } from "misc/qr_utils";
import { deleteLocal } from "misc/list_helper";
import FormButton from "components/form/FormButton";

const Users2Screen: React.FC<DefaultAndAuthProps> = (props) => {
  const {
    admin,
    account,
    home,
    homes,
    setAccount,
    setHome,
    loader,

    globalAppMode,
  } = props;

  const orgName = home?.name || account?.name || "";
  const title = _("user", "list_title_prefix") + orgName;
  const subtitle =
    globalAppMode === "HEALTH"
      ? _("user", "list_subtitle_health")
      : _("user", "list_subtitle_hr");

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item?: User | null;
  }>({ open: false, item: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [newIdentifier, setNewIdentifier] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [qrImgData, setQrImgData] = useState("");
  const [qrHomeName, setQrHomeName] = useState("");

  const selectedOrgId = account?.uuid || "";
  const selectedHomeId = home?.uuid || "";

  const subOrgs: Home[] = useMemo(() => homes || [], [homes]);

  const loadOrganisations = useCallback(() => {
    apiGetAccountList(admin, globalAppMode)
      .then((response) => {
        const orgs: Organisation[] = response.data;
        setOrganisations(orgs);
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.error;
        console.log(errorCode);
      });
  }, [admin, globalAppMode]);

  const handleSelectOrganisation = (uuid: string) => {
    const org = organisations.find((o) => o.uuid === uuid);
    if (org) {
      setAccount(org as any);
      setHome(undefined);
    }
  };

  const handleSelectSubOrganisation = (uuid: string) => {
    const h = subOrgs.find((x) => x.uuid === uuid);
    if (h) {
      setHome(h);
    }
  };

  const columns: Column[] = [
    { key: "identifier", label: _("staff", "editor_label_identifier") },
    { key: "signin_code", label: "Sign-in Code" },
  ];

  const filteredData = users.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.identifier?.toLowerCase().includes(q) ||
      item.signin_code?.toLowerCase().includes(q)
    );
  });

  const handleDelete = (item: User) => {
    setDeleteDialog({ open: true, item });
  };

  const handleRowClick = (item: User) => {
    if (!item) return;
    // Show QR popup instead of navigating to edit
    const code = item.signin_code || item.identifier || "";
    const dataUrl = getQRImageDataURL(code || "");
    setQrImgData(dataUrl);
    setQrHomeName(item.identifier || "");
    setShowQr(true);
  };

  const confirmDelete = () => {
    const item = deleteDialog.item;
    if (!item) return;
    loader.setLoading();
    apiUserDelete(admin, item.uuid)
      .then(() => {
        loader.reset();
        deleteLocal(users, item.uuid, setUsers);
        setDeleteDialog({ open: false, item: null });
      })
      .catch((error) => {
        loader.reset();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  const actionLoadUsers = useCallback(() => {
    if (!home) return;

    apiGetUserList(admin, home.uuid)
      .then((response) => {
        setUsers(response.data);
      })
      .catch((error) => {
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  }, [admin, home]);

  const handleOpenCreateModal = () => {
    setCreateOpen(true);
  };

  const handleConfirmCreate = () => {
    if (!home) return;
    const value = newIdentifier.trim();
    if (!value) return;
    loader.setLoading();
    apiUserCreate(admin, home.uuid, value)
      .then((response) => {
        loader.reset();
        setUsers((prev) => [...prev, response.data]);
        setCreateOpen(false);
        setNewIdentifier("");
      })
      .catch((error) => {
        loader.reset();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  useEffect(() => {
    if (admin?.access_token && account) {
      setUsers([]);
      actionLoadUsers();
    }
  }, [account, actionLoadUsers, admin?.access_token, home]);

  useEffect(() => {
    if (admin?.access_token && admin?.is_super_admin) {
      loadOrganisations();
    }
  }, [admin?.access_token, loadOrganisations, admin?.is_super_admin]);

  return (
    <div className={cssLayout.screen}>
      <PageHeader title={title} subtitle={subtitle} />
      <Box className={css.contentBox}>
        <Box className={css.selectRow}>
          {admin?.is_super_admin && (
            <FormControl fullWidth>
              <InputLabel id="org-select-label">
                {_(
                  "organisation",
                  globalAppMode === "HEALTH"
                    ? "health_organisation_name"
                    : "organisation_name_label",
                )}
              </InputLabel>
              <Select
                labelId="org-select-label"
                value={selectedOrgId}
                label={_(
                  "organisation",
                  globalAppMode === "HEALTH"
                    ? "health_organisation_name"
                    : "organisation_name_label",
                )}
                onChange={(e) =>
                  handleSelectOrganisation(String(e.target.value))
                }
              >
                {organisations.map((org) => (
                  <MenuItem key={org.uuid} value={org.uuid}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl
            fullWidth
            disabled={!selectedOrgId || subOrgs.length === 0}
          >
            <InputLabel id="suborg-select-label">
              {_(
                "organisation",
                globalAppMode === "HEALTH"
                  ? "health_sub_organisation_name_label"
                  : "sub_organisation_name_label",
              )}
            </InputLabel>
            <Select
              labelId="suborg-select-label"
              value={selectedHomeId}
              label={_(
                "organisation",
                globalAppMode === "HEALTH"
                  ? "health_sub_organisation_name_label"
                  : "sub_organisation_name_label",
              )}
              onChange={(e) =>
                handleSelectSubOrganisation(String(e.target.value))
              }
            >
              {subOrgs.map((h) => (
                <MenuItem key={h.uuid} value={h.uuid}>
                  {h.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {users.length === 0 && (
          <div className={css.emptyState}>
            {_("user", "empty_state_message")}
          </div>
        )}
        <Box className={css.searchRow}>
          <Search
            placeholder={_("general", "search")}
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <FormButton
            label={_("organisation", "create_new")}
            onSubmit={handleOpenCreateModal}
            inline={true}
            variant="contained"
            color="darkerGreen"
          />
        </Box>

        {users.length > 0 && (
          <div className={css.tableBox}>
            <DataTable
              columns={columns}
              data={filteredData}
              onDelete={handleDelete}
              onRowClick={handleRowClick}
            />
          </div>
        )}
      </Box>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        BackdropProps={{
          sx: {
            backgroundColor: "#4e4c4c80",
            backdropFilter: "blur(16px)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#FAFAFA",
            borderRadius: "16px",
            minWidth: { xs: "90%", sm: "600px" },
            padding: "16px",
          },
        }}
      >
        <DialogContent>
          <Box className={css.modalHeader}>
            <div className={css.modalTitle}>
              {_("user", "add_title_prefix") + (home?.name || "...")}
            </div>
            <Button
              onClick={() => setCreateOpen(false)}
              className={css.iconButton}
            >
              <CloseIcon />
            </Button>
          </Box>

          <Box className={css.modalBody}>
            <TextInput
              label={_("user", "identifier_label")}
              initialValue={newIdentifier}
              updateValue={setNewIdentifier}
              isRequired={true}
              placeholder={_("user", "placeholder_identifier")}
              labelOutside={true}
            />
          </Box>
          <DialogActions className={css.modalActions}>
            <FormButton
              label={_("general", "btn_cancel")}
              onSubmit={() => setCreateOpen(false)}
              heightPx={50}
              color="white"
            />
            <FormButton
              label={_("general", "btn_save")}
              onSubmit={handleConfirmCreate}
              disabled={!home || !newIdentifier.trim()}
              color="darkerGreen"
              heightPx={50}
            />
          </DialogActions>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={deleteDialog.open}
        title={_("general", "btn_delete")}
        message={_("general", "confirm_delete")}
        itemName={deleteDialog.item?.identifier}
        confirmLabel={_("general", "btn_delete")}
        cancelLabel={_("general", "btn_cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, item: null })}
      />
      {showQr && (
        <QrPopup
          homeName={qrHomeName}
          qrImgData={qrImgData}
          actionUpdateSigninQR={() => {}}
          setShowQr={setShowQr}
          _={_}
          savePNGFile={savePNGFile}
        />
      )}
    </div>
  );
};

export default Users2Screen;
